/**
 * SnfWalkerPanel — interactive B1 → B2 → B3 → B4 walk.
 *
 * The Seam Normal Form (SNF) presents every bridge as a four-phase
 * backbone: B1 (WITNESS — evidence), B2 (AUTHORIZE — η_s consumed),
 * B3 (EXECUTE — composite ε∘η), B4 (ACCOUNT — ε_s emitted). T4
 * (phase-index diagonality) pins sdpIndex(B2) ≡ sdpIndex(B4) — the
 * audit log cannot retroactively claim a different SDP than the one
 * that actually authorized the action.
 *
 * Mirrors chapter 4 §4.4 / Appendix D.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useState } from "react";
import { SDPS } from "../data/sdps.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import {
  checkSnf,
  singleSdpSnf,
  type SnfChain,
  type SnfVerdict,
} from "../algorithms/bridge.js";
import {
  formatGrade,
  gradeAdd,
  neutralGrade,
  singletonGrade,
  type SdpCode,
} from "../algorithms/grade.js";
import { Arrow, PhaseCard, TruthRow } from "./snf-walker/SnfDiagramParts.js";

interface SdpLookupEntry {
  readonly code: SdpCode;
  readonly name: string;
  readonly color: string;
}

const SDPS_LOOKUP: readonly SdpLookupEntry[] = SDPS.map((s) => ({
  code: s.code,
  name: s.name,
  color: s.color,
}));

function sdpColor(code: SdpCode): string {
  return SDPS_LOOKUP.find((s) => s.code === code)?.color ?? panelTokens.accent;
}

type B3Mode = "composite" | "broken";

interface PresetSpec {
  readonly id: string;
  readonly label: string;
  readonly b2Sdp: SdpCode;
  readonly b4Sdp: SdpCode;
  readonly b3Mode: B3Mode;
  readonly note: string;
}

const PRESETS: readonly PresetSpec[] = [
  {
    id: "honest",
    label: "Honest single-SDP chain (Dt)",
    b2Sdp: "Dt",
    b4Sdp: "Dt",
    b3Mode: "composite",
    note: 'singleSdpSnf("Dt") — B2 and B4 share SDP Dt; B3 carries the composite.',
  },
  {
    id: "cross-sdp",
    label: "Cross-SDP forgery (B2=At, B4=Dt)",
    b2Sdp: "At",
    b4Sdp: "Dt",
    b3Mode: "composite",
    note: "B2 authorizes with At but B4 attests with Dt — refused by T4.",
  },
  {
    id: "b3-mismatch",
    label: "B3 mismatch (broken composite)",
    b2Sdp: "Dt",
    b4Sdp: "Dt",
    b3Mode: "broken",
    note: "B3 deliberately set to neutral — composite missing, refused by B3-mismatch.",
  },
];

function buildChain(b2Sdp: SdpCode, b4Sdp: SdpCode, b3Mode: B3Mode): SnfChain {
  const b2Grade = singletonGrade(b2Sdp, "eta");
  const b4Grade = singletonGrade(b4Sdp, "epsilon");
  const composite =
    b3Mode === "broken" ? neutralGrade() : gradeAdd(b2Grade, b4Grade);
  return {
    b1Witness: `evidence-${b2Sdp}-${b4Sdp}`,
    b2Grade,
    b3Composite: composite,
    b4Grade,
  };
}

export function SnfWalkerPanel(): React.JSX.Element {
  const [b2Sdp, setB2Sdp] = useState<SdpCode>("Dt");
  const [b4Sdp, setB4Sdp] = useState<SdpCode>("Dt");
  const [b3Mode, setB3Mode] = useState<B3Mode>("composite");

  const chain = useMemo(
    () => buildChain(b2Sdp, b4Sdp, b3Mode),
    [b2Sdp, b4Sdp, b3Mode],
  );
  const verdict: SnfVerdict = useMemo(() => checkSnf(chain), [chain]);

  const applyPreset = useCallback((id: string) => {
    const preset = PRESETS.find((p) => p.id === id);
    if (!preset) return;
    setB2Sdp(preset.b2Sdp);
    setB4Sdp(preset.b4Sdp);
    setB3Mode(preset.b3Mode);
  }, []);

  const honestChain = useMemo(() => singleSdpSnf("Dt"), []);

  const sdpChipItems = useMemo(
    () =>
      SDPS_LOOKUP.map((s) => ({
        id: s.code,
        label: `${s.code} · ${s.name}`,
        color: s.color,
      })),
    [],
  );

  const verdictBannerProps = useMemo(() => {
    if (verdict.outcome === "VALID") {
      return {
        outcome: "ADMITTED" as const,
        tag: "SNF VALID · T4 satisfied",
        explanation: verdict.explanation,
      };
    }
    return {
      outcome: "REFUSED" as const,
      tag:
        verdict.refusedBy === "T4"
          ? "refused by T4 · phase-index diagonality"
          : "refused by B3-mismatch · composite missing",
      explanation: verdict.explanation,
    };
  }, [verdict]);

  return (
    <PanelSurface.PanelShell
      title="SNF Walker"
      subtitle="B1 → B2 → B3 → B4 · THE FOUR-PHASE BACKBONE · T4 PHASE-INDEX DIAGONALITY"
      accentColor="#fbbf24"
    >
      <PanelSurface.Card glowColor="#fbbf2444">
        <PanelSurface.SectionHeading>
          Seam Normal Form — the four-phase backbone
        </PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[14px] leading-[1.65]">
          Every bridge can be unfolded into four phases. <strong>B1</strong>{" "}
          (WITNESS) is the raw evidence the body carries. <strong>B2</strong>{" "}
          (AUTHORIZE) is the unit <PanelSurface.InlineCode>η_s</PanelSurface.InlineCode> consumed at the
          seam. <strong>B3</strong> (EXECUTE) is the composite{" "}
          <PanelSurface.InlineCode>ε∘η</PanelSurface.InlineCode> — the bridge actually crossing.{" "}
          <strong>B4</strong> (ACCOUNT) is the counit{" "}
          <PanelSurface.InlineCode>ε_s</PanelSurface.InlineCode> emitted into the audit log.
        </p>
        <p className="mt-2.5 text-[13.5px] leading-[1.65] text-[var(--tcc-muted)]">
          <strong>T4 (phase-index diagonality):</strong>{" "}
          <PanelSurface.InlineCode>sdpIndex(B2) ≡ sdpIndex(B4)</PanelSurface.InlineCode>. The η/ε phase
          components legitimately differ across B2 and B4; the discharging SDP
          index does not. An audit log cannot retroactively claim a different
          SDP was used than what actually authorized the action.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Controls</PanelSurface.SectionHeading>
        <div className="mt-2.5 grid grid-cols-[minmax(0,1fr)] gap-[18px]">
          <div>
            <div className="mb-2 font-mono text-[11.5px] tracking-[0.06em] text-[var(--tcc-muted)]">
              B2 · AUTHORIZE SDP
            </div>
            <PanelSurface.ChipRow
              items={sdpChipItems}
              activeId={b2Sdp}
              onChange={(id) => {
                setB2Sdp(id);
              }}
              ariaLabel="B2 SDP choice"
            />
          </div>
          <div>
            <div className="mb-2 font-mono text-[11.5px] tracking-[0.06em] text-[var(--tcc-muted)]">
              B4 · ACCOUNT SDP
            </div>
            <PanelSurface.ChipRow
              items={sdpChipItems}
              activeId={b4Sdp}
              onChange={(id) => {
                setB4Sdp(id);
              }}
              ariaLabel="B4 SDP choice"
            />
          </div>
        </div>

        <div className="mt-[18px] flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              aria-label={`Apply preset: ${preset.label}`}
              onClick={() => {
                applyPreset(preset.id);
              }}
              title={preset.note}
              className="cursor-pointer rounded-full border border-[var(--tcc-border)] bg-[rgba(5,8,16,0.55)] px-3 py-2 text-[12.5px] font-semibold text-[var(--tcc-text)]"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Chain B1 → B2 → B3 → B4</PanelSurface.SectionHeading>
        <div className="mt-3.5 grid grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)_20px_minmax(0,1fr)_20px_minmax(0,1fr)] items-stretch gap-2">
          <PhaseCard
            phase="B1"
            phaseName="WITNESS"
            accent={panelTokens.accentCyan}
            caption="evidence"
            grade={null}
            mono={chain.b1Witness}
          />
          <Arrow />
          <PhaseCard
            phase="B2"
            phaseName="AUTHORIZE"
            accent={sdpColor(b2Sdp)}
            caption={formatGrade(chain.b2Grade)}
            grade={chain.b2Grade}
          />
          <Arrow />
          <PhaseCard
            phase="B3"
            phaseName="EXECUTE"
            accent={panelTokens.accentPurple}
            caption={formatGrade(chain.b3Composite)}
            grade={chain.b3Composite}
          />
          <Arrow />
          <PhaseCard
            phase="B4"
            phaseName="ACCOUNT"
            accent={sdpColor(b4Sdp)}
            caption={formatGrade(chain.b4Grade)}
            grade={chain.b4Grade}
          />
        </div>

        <div className="mt-[18px]">
          <PanelSurface.VerdictBanner
            outcome={verdictBannerProps.outcome}
            tag={verdictBannerProps.tag}
            explanation={verdictBannerProps.explanation}
          />
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>What T4 actually says</PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[13.5px] leading-[1.65]">
          An audit log cannot retroactively claim a different SDP was used than
          what actually authorized the action. The η/ε phase components
          legitimately differ across B2 and B4; the SDP index is invariant under
          T4.
        </p>
        <div className="mt-4 overflow-hidden rounded-xl border border-[var(--tcc-border)]">
          <div className="grid grid-cols-[minmax(0,220px)_minmax(0,1fr)_minmax(0,1fr)] border-b border-[var(--tcc-border)] bg-[rgba(5,8,16,0.65)] px-3.5 py-2.5 font-mono text-[11.5px] uppercase tracking-[0.06em] text-[var(--tcc-muted)]">
            <span>Quantity</span>
            <span>at B2 vs B4</span>
            <span>T4 verdict</span>
          </div>
          <TruthRow
            quantity="η/ε components"
            atB2vsB4="differ — η on B2, ε on B4"
            verdict="legitimately free to differ"
            verdictColor={panelTokens.accentGreen}
          />
          <TruthRow
            quantity="sdpIndex(grade)"
            atB2vsB4="must agree across B2 and B4"
            verdict="invariant under T4"
            verdictColor={panelTokens.accentAmber}
          />
        </div>
        <div className="mt-3.5 flex flex-wrap gap-2.5">
          {PanelSurface.badge(
            `honest chain: ${formatGrade(honestChain.b2Grade)} ⇒ ${formatGrade(honestChain.b4Grade)}`,
            panelTokens.accentGreen,
          )}
          {PanelSurface.badge("sdpIndex invariant", panelTokens.accentAmber)}
        </div>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
